import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Observable, combineLatest, filter, from, map, merge, take, takeUntil, withLatestFrom } from "rxjs";
import { Select, ofActionSuccessful } from "@ngxs/store";
import { TranslateModule } from "@ngx-translate/core";

import { DataGridComponent } from "@app/shared/components/smart-data-grid/data-grid/data-grid.component";
import { AppDataState } from "@app/shared/stores/app-data.state";
import { GetAward, ImportAwardData } from "@app/shared/actions/app-data.action";
import { ExcelSheetName, NgxsActionName, NgxsFormPath } from "@app/shared/constants";
import { SearchByItem, WebApiResponse, WebApiResponseBase } from "@app/shared/classes/general";
import { AngularMaterialModule } from "@app/shared/modules/angular-material.module";
import { LoadingSpinnerComponent } from "@app/shared/components/loading-spinner/loading-spinner.component";
import { SmartSearchFormComponent } from "@app/shared/components/smart-search-form/smart-search-form.component";
import * as AppDataAction from "@app/shared/actions/app-data.action";
import { MessageDataComponent } from "@app/shared/components/message-data/message-data.component";
import { SpinnerButtonComponent } from "@app/shared/components/spinner-button/spinner-button.component";
import { PageBasedComponent } from "@app/pages/page.based.component";
import { ImportDataResultComponent } from "@app/pages/import-data-result/import-data-result.component";
import { ImportResult } from "@app/shared/classes/import-result";
import { DataGridColumnDef, GetAwardSearchByList } from "./award.constant";
import { Award } from "@app/shared/classes/award";
import { CommonPageFilterSetting, CommonSearchCriteria, CreatePageFilterSettingWithLstChangeOnSort } from "@app/shared/models/common-data.model";
import { CommonModalDialogComponent } from "@app/shared/components/common-modal-dialog/common-modal-dialog.component";
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from "@app/shared/components/common-modal-dialog/common-modal-dialog.constant";
import { AwardEditModalDialogComponent } from "@app/shared/components/edit-modal-dialog/award-edit-modal-dialog/award-edit-modal-dialog.component";
import { ExcelImportButtonComponent } from "@app/shared/components/excel-import-button/excel-import-button.component";
import { CsvImportType } from "@app/shared/enums";
import { DisableDeleteButtonDirective } from "@app/shared/directive/disable-delete-button.directive";
import { DisableEditButtonDirective } from "@app/shared/directive/disable-edit-button.directive";

@Component({
  selector: "app-award",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingSpinnerComponent,
    DataGridComponent,
    MessageDataComponent,
    CommonModule,
    TranslateModule,
    ExcelImportButtonComponent,
    AngularMaterialModule,
    SmartSearchFormComponent,
    SpinnerButtonComponent,
    ImportDataResultComponent,
    DisableDeleteButtonDirective,
    DisableEditButtonDirective,
  ],
  templateUrl: "./award.component.html",
  styleUrl: "./award.component.css",
})
export class AwardComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.AWARD_SEARCH_FORM;
  selectedItems: Award[] = [];

  dataSource$: Observable<Award[]>;
  awardSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentAwardSearchBy) currentAwardSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_AWARD)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_AWARD)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_AWARD)) response$: Observable<WebApiResponse<Award[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_AWARD)) errorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskExecuting(NgxsActionName.IMPORT_AWARD_DATA)) importing$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.IMPORT_AWARD_DATA)) importResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_AWARD_DATA)) importErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_AWARD)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_AWARD;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentAwardSearchBy$.pipe(take(1)).subscribe((searchCriteria) => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map((errorResponse) => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.data)
    );
    this.totalCount$ = this.response$.pipe(
      filter(Boolean),
      map((c) => c.totalCount)
    );

    this.awardSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$]).pipe(map((c) => GetAwardSearchByList(c)));

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.ImportAwardData), withLatestFrom(this.importResponse$, this.importErrorResponse$, this.profileMe$), takeUntil(this.destroy$))
      .subscribe(([_, importResponse, importErrorResponse, profileMe]) =>
        this.showImportResult(importResponse as ImportResult[], importErrorResponse, "ml_143952", "ml_143950", profileMe, this.onSearchWithLastModifyPageFilter)
      );

    this.actions$
      .pipe(ofActionSuccessful(AppDataAction.DeleteAward), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$))
      .subscribe(([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));
  }

  rowSelected = (data: Award[]) => {
    this.selectedItems = data ?? [];
  };

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAward());
  };

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAward(null));
  };

  onSearchWithLastModifyPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new GetAward(CreatePageFilterSettingWithLstChangeOnSort(this.functionName)));
  };

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_AWARD;
    this.store.dispatch(new GetAward(info));
  };

  onImport = (file: File) => {
    this.excelImportService.OnFileSlected(
      file,
      [ExcelSheetName.AWARD],
      (fileData) => {
        var importData = this.csvService.GetImportObjectFromCsvData(CsvImportType.AWARD, fileData);
        if (importData.length === 0) {
          this.showImportEmptyDataResult();
        } else {
          this.store.dispatch(new ImportAwardData(importData));
        }
      },
      this.showImportEmptyDataResult
    );
  };

  onCreate = () => {
    this.dialog.open(AwardEditModalDialogComponent, {
      width: "700px",
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true,
    });
  };

  onEdit = () => {
    this.dialog.open(AwardEditModalDialogComponent, {
      width: "700px",
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true,
    });
  };

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.data !== ModalDialogResult.YES) {
          return;
        }

        this.store.dispatch(new AppDataAction.DeleteAward(this.selectedItems.map((item) => item.award_Id)));
      });
  };
}
